import { AxiosService } from './axios.service'
import { StorageService } from './storage.service'

const baseURL = 'venda-de-passagem'

/**
 * Defines the API methods for ticket sale
 */
export class TicketSaleService {
	/**
	 * Get the origins matching a search term
	 * @return {Promise<any>} The list of origins
	 */
	static getOrigins(query) {
		return AxiosService.getMuviHub('travel/origins', {
			params: {
				query,
				context: 'standard',
				limit: 30,
			},
		}).then(({ data }) => data)
	}

	/**
	 * Get the destinations matching an origin and, optionally, a search term
	 * @return {Promise<any>} The list of destinations
	 */
	static getDestinations(originId, query) {
		return AxiosService.getMuviHub(`travel/origins/${originId}/destinations`, {
			params: {
				query,
				context: 'standard',
			},
		}).then(({ data }) => data)
	}

	/**
	 * Swap the origin and destination
	 * @return {Promise<any>} The new origin, destination and available destinations
	 */
	static swapLocalities({ currentOriginId, currentDestinationId }) {
		return AxiosService.postMuviHub(
			'travel/swap',
			{ currentOriginId, currentDestinationId },
			{ params: { context: 'standard' } }
		).then(({ data }) => data)
	}

	/**
	 * Search the trips
	 * @return {Promise<any>} The outbound and return trips
	 */
	static async searchTrips({
		originId,
		destinationId,
		tripDateGoing,
		tripDateReturn,
	}) {
		const { data } = await AxiosService.getMuviHub('trips/search', {
			params: {
				originId,
				destinationId,
				departureDate: tripDateGoing,
				returnDate: tripDateReturn,
			},
		})

		return data.data
	}

	/**
	 * Get the bus seats
	 * @param {object} params
	 * shape {
	 *  "busToken": "string",
	 *  "direction": "outbound",
	 *  "kind": "outbound",
	 *  "legIndex": 0,
	 *  "selectedPriceId": 0,
	 *  "withInsurance": false
	 * }
	 * @return {Promise<any>} The bus seats information
	 */
	static getBusData(params, requestId) {
		return AxiosService.postKeepFirstMuviHub(
			requestId,
			'trips/seat-map',
			params
		).then(({ data }) => data)
	}

	/**
	 * Finalize the purchase
	 * @return {Promise<any} the purchase result
	 */
	static finalizePurchase(params) {
		return AxiosService.post(`${baseURL}/confirmar-venda`, params).then(
			({ data }) => data
		)
	}

	/**
	 * Book a bus seat
	 * @return {Promise<any} the booking result, shape { reservation, cart }
	 */
	static bookBusSeat(params) {
		const cart = StorageService.cart.get()

		return AxiosService.postMuviHub(`carts/${cart && cart.id}/seats`, params).then(
			({ data }) => data
		)
	}

	/**
	 * Cancel a bus seat booking
	 * @param {object} params Must include transactionId, the reservation id returned by bookBusSeat
	 * @return {Promise<any} the cancelation result
	 */
	static cancelBusSeatBooking(params) {
		const cart = StorageService.cart.get()

		return AxiosService.deleteMuviHub(
			`carts/${cart && cart.id}/seats/${params.transactionId}`
		).then(({ data }) => data)
	}

	/**
	 * Request for the authenticator form
	 * @return {Promise<any} the purchase result
	 */
	static sendGetNetDebitCard(params) {
		return AxiosService.post(
			`${baseURL}/formulario-autenticador`,
			params
		).then(({ data }) => data)
	}

	/**
	 * Submit the PaRes
	 * @return {Promise<any} the purchase result
	 */
	static confirmPaResDebitCard(params) {
		return AxiosService.post(
			`gateway-getnet/confirmar-pagamento-debito`,
			params
		).then(({ data }) => data)
	}

	/**
	 * Get parameters to family discount
	 * @return {Promise<any} the family discount parameters return
	 */
	static getDiscountParameters() {
		return AxiosService.get(`${baseURL}/parametros`).then(({ data }) => data)
	}

	/**
	 * Get bankslip parameters
	 * @return {Promise<any} bankslip parameters
	 */
	static getBankSlipParameters() {
		return AxiosService.get(`${baseURL}/boleto-parametros`).then(
			({ data }) => data
		)
	}

	/**
	 * Get muvicard discount
	 * @return {Promise<any} the family discount parameters return
	 */
	static getMuvicardDiscountParameters(params) {
		return AxiosService.post(`${baseURL}/muvicard-parametros`, params).then(
			({ data }) => data
		)
	}

	/**
	 * Applies the best automatic coupon available for the cart (Bug 7829).
	 * POST with no body - the cart id in the route and the auth header (when
	 * logged in) are the only real inputs; the API cascades user-based then
	 * route-based coupon resolution server-side.
	 * @return {Promise<any}
	 */
	static getAutomaticDiscountCoupon() {
		const cart = StorageService.cart.get()

		return AxiosService.postMuviHub(`carts/${cart && cart.id}/coupon/automatic`).then(
			({ data }) => data
		)
	}
}

export default TicketSaleService
